import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import {
  Component,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { CdkMenuModule } from '@angular/cdk/menu';
import { TooltipModule } from 'primeng/tooltip';
import { SubSectionService } from '../../../../services/builder/sub-section.service';
import { ConnectionPositionPair } from '@angular/cdk/overlay';
import { DragDropService } from '../../../../services/builder/drag-drop.service';
import { BaseWidgetEditComponent } from '../../../new-elements/base-element/base-element-edit.component';
import { DialogModule } from 'primeng/dialog';
import { SubSectionCustomizationPanelComponent } from './sub-section-customization-panel.component';
import { WidgetStructureObject } from '../../../../api/interfaces';
import { NgClass } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'lib-edit-sub-section',
  templateUrl: './sub-section.component.html',
  imports: [
    DragDropModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    CdkMenuModule,
    TooltipModule,
    BaseWidgetEditComponent,
    DialogModule,
    SubSectionCustomizationPanelComponent,
    NgClass,
    ReactiveFormsModule,
    MatMenuModule,
  ],
  providers: [SubSectionService],
})
export class SubSectionEditComponent {
  protected subSectionService = inject(SubSectionService);
  protected dragdropService = inject(DragDropService);

  subSectionId = input.required<string>();
  ref = input.required<HTMLDivElement>();

  inlineTitleEditor = signal(false);
  inlineTitleEditorField =
    viewChild<ElementRef<HTMLInputElement>>('titleInput');
  title = new FormControl('');

  inlineSubTitleEditor = signal(false);
  inlineSubTitleEditorField =
    viewChild<ElementRef<HTMLInputElement>>('subTitleInput');
  subTitle = new FormControl('');

  inlinePointsEditor = signal(false);
  inlinePointsEditorField =
    viewChild<ElementRef<HTMLInputElement>>('pointsInput');
  points = new FormControl(0);

  positions = [
    new ConnectionPositionPair(
      { originX: 'end', originY: 'center' },
      { overlayX: 'end', overlayY: 'center' },
      50,
      0,
    ),
  ];

  constructor() {
    const ref = effect(() => {
      this.subSectionService.uniqueId = this.subSectionId();
      ref.destroy();
    });

    const ref1 = effect(() => {
      const subsection = this.subSectionService.subsection();
      if (!subsection) return;

      this.title.setValue(subsection.customization.title || '');
      this.subTitle.setValue(subsection.customization.subTitle || '');
      this.points.setValue(subsection.customization.points || 0);
      ref1.destroy();
    });

    effect(() => {
      const inlineTitleEditorField = this.inlineTitleEditorField();
      if (!inlineTitleEditorField) return;

      inlineTitleEditorField.nativeElement.focus();
      inlineTitleEditorField.nativeElement.select();
    });

    effect(() => {
      const inlineSubTitleEditorField = this.inlineSubTitleEditorField();
      if (!inlineSubTitleEditorField) return;

      inlineSubTitleEditorField.nativeElement.focus();
      inlineSubTitleEditorField.nativeElement.select();
    });

    effect(() => {
      const inlinePointsEditorField = this.inlinePointsEditorField();
      if (!inlinePointsEditorField) return;

      inlinePointsEditorField.nativeElement.focus();
      inlinePointsEditorField.nativeElement.select();
    });
  }

  removeSubSection() {
    this.subSectionService.removeSubSection();
  }

  drop(event: CdkDragDrop<string[], string, string | WidgetStructureObject>) {
    if (event.previousContainer.id === 'localElements') {
      this.subSectionService.addWidget(
        event.item.data as WidgetStructureObject,
        event.currentIndex,
      );
    } else {
      this.subSectionService.moveWidget(
        event.previousContainer.id,
        event.item.data as string,
        event.currentIndex,
      );
    }
  }

  closeInlineTitleEditor() {
    const customization = this.subSectionService.subsection()?.customization;
    if (!customization) return;

    customization.title = this.title.value || '';
    this.subSectionService.updateSubSection(this.subSectionId(), {
      ...customization,
    });
    this.inlineTitleEditor.set(false);
  }

  closeInlineSubTitleEditor() {
    const customization = this.subSectionService.subsection()?.customization;
    if (!customization) return;

    customization.subTitle = this.subTitle.value || '';
    this.subSectionService.updateSubSection(this.subSectionId(), {
      ...customization,
    });
    this.inlineSubTitleEditor.set(false);
  }

  closeInlinePointsEditor() {
    const customization = this.subSectionService.subsection()?.customization;
    if (!customization) return;

    customization.points = this.points.value || 0;
    this.subSectionService.updateSubSection(this.subSectionId(), {
      ...customization,
    });
    this.inlinePointsEditor.set(false);
  }
}
